// src/features/tickets/components/ProofreadButton.tsx
//
// Geteilter "Pruefen"-Knopf fuer den Mail-Composer. Schickt den aktuellen
// Entwurf in STRUKTURIERTER Form an die serverseitige KI-Pruefung und zeigt das
// Ergebnis als formatierte Vorschau (plus Hinweise) mit Uebernehmen/Verwerfen.
// Kein Auto-Versand.
//
// Verlustfreiheit: Der Editor-Inhalt wird als Quill-Delta gelesen und in eine
// strukturierte Form zerlegt (styles/blocks als opake IDs, Bilder als Marker).
// Die KI bekommt nur die IDs und reicht sie durch; aus der validierten Antwort
// bauen wir programmatisch wieder ein Delta und schreiben es per setContents
// zurueck. Es findet KEIN HTML-Re-Import statt — damit bleibt jede (auch
// kuenftige) Formatierung erhalten. Schlaegt die Validierung fehl, wird nichts
// angewendet und der Editor bleibt unveraendert.
//
// Wird im RichTextEmailEditor eingebunden (beide Composer). Der Editor reicht
// quillRef, mode und (bei 'full') getProofreadContext durch.
//
// onPendingChange meldet dem umgebenden Composer, ob aktuell ein nicht
// uebernommener Vorschlag offen ist (preview-Phase). Damit kann der Composer vor
// dem Senden rueckfragen, statt versehentlich das unkorrigierte Original zu
// verschicken. Ein offener Vorschlag wird zudem automatisch geschlossen, sobald
// sich der Editorinhalt aendert (manuelle Bearbeitung, Uebernehmen oder Leeren
// nach dem Senden), damit kein veralteter Vorschlag stehen bleibt.

import React, { useState, useRef, useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import DOMPurify from 'dompurify';
import { ticketProofreadService } from '../services/TicketProofreadService';
import type {
  ProofreadMode,
  ProofreadContext,
  ProofreadDraftSnapshot,
  ProofreadLine,
  ProofreadRun,
  ProofreadStyleMap,
  ProofreadBlockMap,
  ProofreadEmbed,
  ProofreadAttributes,
  ProofreadHint,
  ProofreadHintType
} from '../services/TicketProofreadService';

interface ProofreadButtonProps {
  /** Ref auf die ReactQuill-Instanz (besitzt getEditor()). */
  quillRef: React.RefObject<any>;
  /** Pruefmodus: 'full' fuer Kundenmails, 'note' fuer interne Notizen. */
  mode: ProofreadMode;
  /** Liefert (nur im 'full'-Modus) den Kontext: Verlauf, Namen, Anhaenge. */
  getProofreadContext?: () => Promise<ProofreadContext>;
  /**
   * Meldet, ob aktuell ein nicht uebernommener KI-Vorschlag offen ist
   * (true waehrend der preview-Phase, sonst false). Der Composer nutzt das fuer
   * eine Rueckfrage vor dem Senden.
   */
  onPendingChange?: (pending: boolean) => void;
}

type ProofreadState =
  | { phase: 'idle' }
  | { phase: 'loading' }
  | { phase: 'preview'; snapshot: ProofreadDraftSnapshot; lines: ProofreadLine[]; hints: ProofreadHint[]; html: string }
  | { phase: 'error'; message: string };

// ============================================================================
// Reine Hilfsfunktionen: Delta <-> strukturierte Form, Validierung, Vorschau
// ============================================================================

// Stabile (schluesselsortierte) Serialisierung fuer das Deduplizieren von
// Attribut-Maps zu IDs.
function stableStringify(obj: Record<string, unknown>): string {
  const keys = Object.keys(obj).sort();
  const sorted: Record<string, unknown> = {};
  for (const k of keys) sorted[k] = obj[k];
  return JSON.stringify(sorted);
}

// Liest das aktuelle Quill-Delta und zerlegt es verlustfrei in die strukturierte
// Form. Inline-Attribute werden je Lauf VERBATIM unter einer Stil-ID abgelegt,
// Block-Attribute je Zeile unter einer Block-ID; Bilder (Embeds) landen unter
// einer Bild-ID im embedById und werden nie an die KI geschickt. Genutzt wird
// Delta.eachLine, das Inline-Ops und Block-Attribute korrekt trennt.
function deltaToStructured(quill: any): ProofreadDraftSnapshot {
  const delta = quill.getContents();

  const styles: ProofreadStyleMap = { S0: {} };
  const blocks: ProofreadBlockMap = { B0: {} };
  const embedById: Record<string, ProofreadEmbed> = {};
  const lines: ProofreadLine[] = [];

  const styleKeyToId = new Map<string, string>([['{}', 'S0']]);
  const blockKeyToId = new Map<string, string>([['{}', 'B0']]);
  let styleCounter = 0;
  let blockCounter = 0;
  let imgCounter = 0;

  const internStyle = (attrs: unknown): string => {
    const obj: ProofreadAttributes = attrs && typeof attrs === 'object' ? (attrs as ProofreadAttributes) : {};
    const key = stableStringify(obj);
    const existing = styleKeyToId.get(key);
    if (existing) return existing;
    styleCounter += 1;
    const id = `S${styleCounter}`;
    styleKeyToId.set(key, id);
    styles[id] = obj;
    return id;
  };

  const internBlock = (attrs: unknown): string => {
    const obj: ProofreadAttributes = attrs && typeof attrs === 'object' ? (attrs as ProofreadAttributes) : {};
    const key = stableStringify(obj);
    const existing = blockKeyToId.get(key);
    if (existing) return existing;
    blockCounter += 1;
    const id = `B${blockCounter}`;
    blockKeyToId.set(key, id);
    blocks[id] = obj;
    return id;
  };

  delta.eachLine((lineDelta: any, blockAttrs: unknown) => {
    const runs: ProofreadRun[] = [];
    const ops = Array.isArray(lineDelta?.ops) ? lineDelta.ops : [];
    for (const op of ops) {
      const insert = op?.insert;
      if (typeof insert === 'string') {
        if (insert.length === 0) continue;
        runs.push({ t: insert, s: internStyle(op.attributes) });
      } else if (insert && typeof insert === 'object') {
        imgCounter += 1;
        const id = `IMG${imgCounter}`;
        embedById[id] = { insert: insert as Record<string, unknown>, attributes: op.attributes };
        runs.push({ img: id });
      }
    }
    lines.push({ b: internBlock(blockAttrs), runs });
  });

  return { styles, blocks, lines, embedById };
}

// Prueft, ob der Entwurf praktisch leer ist (kein Bild, nur Leerraum-Text).
function isSnapshotEmpty(snapshot: ProofreadDraftSnapshot): boolean {
  if (Object.keys(snapshot.embedById).length > 0) return false;
  for (const line of snapshot.lines) {
    for (const run of line.runs) {
      if ('img' in run) return false;
      if (run.t.trim().length > 0) return false;
    }
  }
  return true;
}

// Strikte Validierung der KI-Antwort gegen den Snapshot: nur bekannte Stil-/
// Block-IDs (plus S0/B0), nur bekannte Bild-IDs, und jede Bild-ID genau einmal.
// Schlaegt eine Pruefung fehl, wird nichts uebernommen.
function validateProofreadResult(snapshot: ProofreadDraftSnapshot, lines: unknown): boolean {
  if (!Array.isArray(lines) || lines.length === 0) return false;

  const styleIds = new Set(Object.keys(snapshot.styles));
  styleIds.add('S0');
  const blockIds = new Set(Object.keys(snapshot.blocks));
  blockIds.add('B0');
  const imageIds = new Set(Object.keys(snapshot.embedById));
  const seenImages = new Set<string>();

  for (const line of lines) {
    if (!line || typeof line !== 'object') return false;
    const b = (line as any).b;
    const runs = (line as any).runs;
    if (typeof b !== 'string' || !blockIds.has(b)) return false;
    if (!Array.isArray(runs)) return false;
    for (const run of runs) {
      if (!run || typeof run !== 'object') return false;
      if ('img' in run) {
        const id = (run as any).img;
        if (typeof id !== 'string' || !imageIds.has(id) || seenImages.has(id)) return false;
        seenImages.add(id);
      } else {
        const t = (run as any).t;
        const s = (run as any).s;
        if (typeof t !== 'string') return false;
        if (typeof s !== 'string' || !styleIds.has(s)) return false;
      }
    }
  }

  return seenImages.size === imageIds.size;
}

// Baut aus dem Snapshot und den (validierten) KI-Zeilen die Delta-Ops neu.
// Inline-Attribute kommen aus snapshot.styles, Block-Attribute aus
// snapshot.blocks, Bilder verbatim aus snapshot.embedById. Endet stets mit "\n".
function structuredToDelta(snapshot: ProofreadDraftSnapshot, lines: ProofreadLine[]): any[] {
  const ops: any[] = [];
  for (const line of lines) {
    for (const run of line.runs) {
      if ('img' in run) {
        const embed = snapshot.embedById[run.img];
        if (!embed) continue;
        if (embed.attributes && Object.keys(embed.attributes).length > 0) {
          ops.push({ insert: embed.insert, attributes: embed.attributes });
        } else {
          ops.push({ insert: embed.insert });
        }
      } else {
        if (run.t.length === 0) continue;
        const attrs = run.s === 'S0' ? {} : (snapshot.styles[run.s] || {});
        if (Object.keys(attrs).length > 0) {
          ops.push({ insert: run.t, attributes: attrs });
        } else {
          ops.push({ insert: run.t });
        }
      }
    }
    const blockAttrs = line.b === 'B0' ? {} : (snapshot.blocks[line.b] || {});
    if (Object.keys(blockAttrs).length > 0) {
      ops.push({ insert: '\n', attributes: blockAttrs });
    } else {
      ops.push({ insert: '\n' });
    }
  }
  return ops;
}

// ---- Vorschau-Rendering (rein zur Anzeige; das Anwenden nutzt das Delta) ----

const PREVIEW_SANITIZE_CONFIG = {
  ALLOWED_TAGS: ['p', 'br', 'div', 'span', 'strong', 'em', 'u', 's', 'a', 'ul', 'ol', 'li', 'blockquote', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'img'],
  ALLOWED_ATTR: ['href', 'src', 'style'],
  // Wie die Default-Regel, zusaetzlich data: (fuer die eingebetteten Bild-Vorschauen).
  ALLOWED_URI_REGEXP: /^(?:(?:(?:f|ht)tps?|mailto|tel|callto|sms|cid|xmpp|data):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i
};

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function cssSafe(s: unknown): string {
  return String(s).replace(/[^a-zA-Z0-9#.,()%\-\s]/g, '');
}

function sizeToCss(size: string): string | null {
  if (size === 'small') return '0.75em';
  if (size === 'large') return '1.5em';
  if (size === 'huge') return '2.5em';
  return null;
}

function renderInline(text: string, attrs: ProofreadAttributes): string {
  let html = escapeHtml(text).replace(/\n/g, '<br>');

  const styleParts: string[] = [];
  if (typeof attrs.color === 'string') styleParts.push(`color:${cssSafe(attrs.color)}`);
  if (typeof attrs.background === 'string') styleParts.push(`background-color:${cssSafe(attrs.background)}`);
  if (typeof attrs.size === 'string') {
    const sz = sizeToCss(attrs.size);
    if (sz) styleParts.push(`font-size:${sz}`);
  }
  if (typeof attrs.font === 'string') styleParts.push(`font-family:${cssSafe(attrs.font)}`);
  if (styleParts.length > 0) html = `<span style="${styleParts.join(';')}">${html}</span>`;

  if (attrs.bold) html = `<strong>${html}</strong>`;
  if (attrs.italic) html = `<em>${html}</em>`;
  if (attrs.underline) html = `<u>${html}</u>`;
  if (attrs.strike) html = `<s>${html}</s>`;
  if (typeof attrs.link === 'string') html = `<a href="${escapeAttr(attrs.link)}">${html}</a>`;

  return html;
}

function renderRuns(snapshot: ProofreadDraftSnapshot, runs: ProofreadRun[]): string {
  let out = '';
  for (const run of runs) {
    if ('img' in run) {
      const embed = snapshot.embedById[run.img];
      const src = embed && embed.insert && typeof embed.insert.image === 'string' ? embed.insert.image : '';
      if (src) out += `<img src="${escapeAttr(src)}" style="max-width:100%;height:auto;" />`;
    } else {
      const attrs = run.s === 'S0' ? {} : (snapshot.styles[run.s] || {});
      out += renderInline(run.t, attrs);
    }
  }
  return out;
}

function renderBlockLine(snapshot: ProofreadDraftSnapshot, line: ProofreadLine, blockAttrs: ProofreadAttributes): string {
  const inner = renderRuns(snapshot, line.runs) || '<br>';

  const styleParts: string[] = [];
  if (typeof blockAttrs.align === 'string') styleParts.push(`text-align:${cssSafe(blockAttrs.align)}`);
  if (typeof blockAttrs.indent === 'number' && blockAttrs.indent > 0) styleParts.push(`margin-left:${blockAttrs.indent * 1.5}em`);
  const styleAttr = styleParts.length ? ` style="${styleParts.join(';')}"` : '';

  if (blockAttrs.blockquote) {
    return `<blockquote style="border-left:3px solid #D1D5DB;margin:0 0 8px;padding-left:10px;color:#4B5563;">${inner}</blockquote>`;
  }
  if (typeof blockAttrs.header === 'number' && blockAttrs.header >= 1 && blockAttrs.header <= 6) {
    const h = blockAttrs.header;
    return `<h${h}${styleAttr}>${inner}</h${h}>`;
  }
  return `<div${styleAttr}>${inner}</div>`;
}

// Erzeugt eine formatierte HTML-Vorschau aus der strukturierten Form. Listen
// werden aus aufeinanderfolgenden Listenzeilen gleichen Typs gruppiert. Reines
// Anzeige-HTML, das anschliessend mit DOMPurify gefiltert wird.
function structuredToHtml(snapshot: ProofreadDraftSnapshot, lines: ProofreadLine[]): string {
  let html = '';
  let i = 0;
  while (i < lines.length) {
    const line = lines[i];
    const blockAttrs = line.b === 'B0' ? {} : (snapshot.blocks[line.b] || {});
    const listType = typeof blockAttrs.list === 'string' ? blockAttrs.list : null;

    if (listType) {
      const tag = listType === 'ordered' ? 'ol' : 'ul';
      let items = '';
      while (i < lines.length) {
        const l = lines[i];
        const ba = l.b === 'B0' ? {} : (snapshot.blocks[l.b] || {});
        const lt = typeof ba.list === 'string' ? ba.list : null;
        const curTag = lt === 'ordered' ? 'ol' : 'ul';
        if (!lt || curTag !== tag) break;
        items += `<li>${renderRuns(snapshot, l.runs) || '<br>'}</li>`;
        i += 1;
      }
      html += `<${tag} style="margin:0 0 8px 1.2em;padding:0;">${items}</${tag}>`;
    } else {
      html += renderBlockLine(snapshot, line, blockAttrs);
      i += 1;
    }
  }
  return html;
}

function renderPreviewHtml(snapshot: ProofreadDraftSnapshot, lines: ProofreadLine[]): string {
  const raw = structuredToHtml(snapshot, lines);
  return DOMPurify.sanitize(raw, PREVIEW_SANITIZE_CONFIG);
}

function hintLabel(type: ProofreadHintType): string {
  switch (type) {
    case 'name_corrected': return 'Name korrigiert';
    case 'missing_closing': return 'Grußformel';
    case 'unanswered_question': return 'Mögliche offene Frage';
    case 'missing_attachment': return 'Anhang erwähnt';
    case 'conflict': return 'Möglicher Konflikt';
    default: return 'Hinweis';
  }
}

// ============================================================================
// Komponente
// ============================================================================

export const ProofreadButton: React.FC<ProofreadButtonProps> = ({ quillRef, mode, getProofreadContext, onPendingChange }) => {
  const [state, setState] = useState<ProofreadState>({ phase: 'idle' });

  // Meldet dem umgebenden Composer, ob aktuell ein nicht uebernommener Vorschlag
  // offen ist. Ueber einen Ref gehalten, damit der Melde-Effekt nur von
  // state.phase abhaengt und nicht bei jeder neuen Callback-Identitaet neu laeuft.
  const onPendingChangeRef = useRef(onPendingChange);
  useEffect(() => {
    onPendingChangeRef.current = onPendingChange;
  });
  useEffect(() => {
    onPendingChangeRef.current?.(state.phase === 'preview');
  }, [state.phase]);

  const getQuill = (): any | null => {
    const editor = quillRef.current;
    return editor && typeof editor.getEditor === 'function' ? editor.getEditor() : null;
  };

  // Schliesst einen offenen Vorschlag automatisch, sobald sich der Editorinhalt
  // aendert (manuelle Bearbeitung, Uebernehmen oder Leeren nach dem Senden). So
  // bleibt kein veralteter Vorschlag stehen, der nicht mehr zum Text passt oder
  // beim naechsten Senden eine falsche Rueckfrage ausloesen wuerde.
  useEffect(() => {
    const editor = quillRef.current;
    const quill = editor && typeof editor.getEditor === 'function' ? editor.getEditor() : null;
    if (!quill || typeof quill.on !== 'function') return;
    const handleTextChange = () => {
      setState(prev => (prev.phase === 'preview' ? { phase: 'idle' } : prev));
    };
    quill.on('text-change', handleTextChange);
    return () => {
      if (typeof quill.off === 'function') quill.off('text-change', handleTextChange);
    };
  }, [quillRef]);

  const runCheck = async () => {
    const quill = getQuill();
    if (!quill) {
      setState({ phase: 'error', message: 'Editor ist nicht bereit.' });
      return;
    }

    let snapshot: ProofreadDraftSnapshot;
    try {
      snapshot = deltaToStructured(quill);
    } catch (e) {
      setState({ phase: 'error', message: 'Der Entwurf konnte nicht gelesen werden.' });
      return;
    }

    if (isSnapshotEmpty(snapshot)) {
      setState({ phase: 'error', message: 'Bitte zuerst einen Text eingeben.' });
      return;
    }

    setState({ phase: 'loading' });

    let context: ProofreadContext | null = null;
    if (mode === 'full' && getProofreadContext) {
      try {
        context = await getProofreadContext();
      } catch (e) {
        context = null;
      }
    }

    const result = await ticketProofreadService.proofread({
      mode,
      styles: snapshot.styles,
      blocks: snapshot.blocks,
      lines: snapshot.lines,
      context
    });

    if (!result.success || !Array.isArray(result.lines)) {
      setState({ phase: 'error', message: result.error || 'Prüfung fehlgeschlagen.' });
      return;
    }

    if (!validateProofreadResult(snapshot, result.lines)) {
      setState({ phase: 'error', message: 'Die Prüfung lieferte kein übernehmbares Ergebnis. Bitte erneut versuchen.' });
      return;
    }

    let html = '';
    try {
      html = renderPreviewHtml(snapshot, result.lines);
    } catch (e) {
      html = '';
    }

    setState({ phase: 'preview', snapshot, lines: result.lines, hints: result.hints || [], html });
  };

  const accept = () => {
    if (state.phase !== 'preview') {
      setState({ phase: 'idle' });
      return;
    }
    const quill = getQuill();
    if (!quill) {
      setState({ phase: 'error', message: 'Editor ist nicht bereit.' });
      return;
    }
    try {
      const ops = structuredToDelta(state.snapshot, state.lines);
      const oldDelta = quill.getContents();
      const Delta = oldDelta.constructor;
      const newDelta = new Delta(ops);
      quill.setContents(newDelta, 'user');
    } catch (e) {
      setState({ phase: 'error', message: 'Das Ergebnis konnte nicht übernommen werden.' });
      return;
    }
    setState({ phase: 'idle' });
  };

  const dismiss = () => setState({ phase: 'idle' });

  const buttonLabel = mode === 'note' ? 'Rechtschreibung prüfen' : 'Rechtschreibung & Stil prüfen';

  return (
    <div style={{ marginTop: '8px' }}>
      <button
        type="button"
        onClick={runCheck}
        disabled={state.phase === 'loading'}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '13px',
          fontWeight: '500',
          color: '#4F46E5',
          backgroundColor: '#EEF2FF',
          border: '1px solid #C7D2FE',
          borderRadius: '6px',
          padding: '6px 10px',
          cursor: state.phase === 'loading' ? 'default' : 'pointer',
          opacity: state.phase === 'loading' ? 0.7 : 1
        }}
      >
        <Sparkles size={14} />
        {state.phase === 'loading' ? 'Prüfe…' : buttonLabel}
      </button>

      {state.phase === 'error' && (
        <div style={{
          marginTop: '8px',
          fontSize: '13px',
          color: '#B91C1C',
          backgroundColor: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '6px',
          padding: '8px 10px'
        }}>
          {state.message}
        </div>
      )}

      {state.phase === 'preview' && (
        <div style={{
          marginTop: '8px',
          border: '1px solid #C7D2FE',
          borderRadius: '6px',
          overflow: 'hidden'
        }}>
          <div style={{
            fontSize: '12px',
            fontWeight: '600',
            color: '#3730A3',
            backgroundColor: '#EEF2FF',
            padding: '6px 10px'
          }}>
            Korrigierter Text — Vorschau
          </div>

          {state.hints.length > 0 && (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '6px',
              padding: '8px 10px',
              borderBottom: '1px solid #E5E7EB'
            }}>
              {state.hints.map((hint, idx) => {
                const isConflict = hint.type === 'conflict';
                return (
                  <div key={idx} style={{
                    fontSize: '12px',
                    color: isConflict ? '#7F1D1D' : '#92400E',
                    backgroundColor: isConflict ? '#FEF2F2' : '#FFFBEB',
                    border: `1px solid ${isConflict ? '#FECACA' : '#FDE68A'}`,
                    borderRadius: '6px',
                    padding: '6px 8px',
                    lineHeight: '1.45'
                  }}>
                    <span style={{ fontWeight: 600 }}>{hintLabel(hint.type)}:</span>{' '}
                    {hint.message || ''}
                  </div>
                );
              })}
            </div>
          )}

          <div
            style={{
              fontSize: '14px',
              color: '#111827',
              padding: '10px',
              maxHeight: '240px',
              overflowY: 'auto',
              lineHeight: '1.5'
            }}
            dangerouslySetInnerHTML={{ __html: state.html }}
          />

          <div style={{
            display: 'flex',
            gap: '8px',
            justifyContent: 'flex-end',
            padding: '8px 10px',
            borderTop: '1px solid #E5E7EB',
            backgroundColor: '#FAFAFA'
          }}>
            <button
              type="button"
              onClick={dismiss}
              style={{
                fontSize: '13px',
                color: '#374151',
                backgroundColor: 'white',
                border: '1px solid #D1D5DB',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer'
              }}
            >
              Verwerfen
            </button>
            <button
              type="button"
              onClick={accept}
              style={{
                fontSize: '13px',
                fontWeight: '500',
                color: 'white',
                backgroundColor: '#4F46E5',
                border: '1px solid #4F46E5',
                borderRadius: '6px',
                padding: '6px 12px',
                cursor: 'pointer'
              }}
            >
              Übernehmen
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
